// GT-327: milliseconds the page was visible during the current story. The wall-clock
// `sessionTime` counts a tab left open over lunch; this does not. Uses Date.now() deltas, not
// timer ticks, because Chrome throttles timers in hidden tabs.
//
// Product rule: the only non-active time is the tab being hidden. Short pauses, interventions
// and a visibly silent student all count. A visible page behind another focused window counts
// too (blur is not handled).
//
// Same lifetime as sessionTime: start() at story start resets to 0, stop() at story end returns
// the final value for the terminal write. read() is only defined between the two, so a heartbeat
// from a story that never called start() sends nothing instead of another story's total. So
// activeTime <= sessionTime on the same row, and a testlet or relevel transition never carries
// time into the next activity. States: not started -> running <-> hidden -> stopped.
//
// Known limit: a lost hidden transition (the lid closes and no visibilitychange fires before
// suspend) counts the sleep as active time. No event shows it; gap detection is a product call.
let started = false;
let accumulatedMs = 0;
let runningSince = null;

// Close the running segment. Clamped at 0: Date.now() is wall clock and can step backwards on an
// NTP or manual time change.
function fold() {
  if (runningSince === null) return;
  accumulatedMs += Math.max(0, Date.now() - runningSince);
  runningSince = null;
}

// Apply document.hidden: pause, or (re)start the running segment. No-op outside start()..stop().
// Reads document.hidden live, not a flag from the last event, because those events can be lost:
// each read() then recovers the clock on the next phrase heartbeat.
function settle() {
  if (!started) return;
  if (typeof document !== 'undefined' && document.hidden) {
    fold();
  } else if (runningSince === null) {
    runningSince = Date.now();
  }
}

export const engagementClock = {
  start() {
    accumulatedMs = 0;
    runningSince = null;
    started = true;
    settle();
  },
  // Story end. Returns the final value; read() is undefined from here until the next start().
  stop() {
    if (!started) return undefined;
    fold();
    started = false;
    return accumulatedMs;
  },
  // Snapshot for the per-phrase heartbeat. Checkpoints the running segment and keeps it running.
  // undefined outside start()..stop(): JSON.stringify drops the variable and the SRS resolver
  // leaves the column unset.
  read() {
    if (!started) return undefined;
    fold();
    settle();
    return accumulatedMs;
  },
};

if (typeof document !== 'undefined') {
  // Exact transitions when these fire. visibilitychange does not always fire on wake from sleep
  // or a bfcache restore (see AppContainer), so focus and pageshow re-check too. settle() is a
  // no-op when nothing changed. Singleton; never removed.
  document.addEventListener('visibilitychange', settle);
  window.addEventListener('focus', settle);
  window.addEventListener('pageshow', settle);
}
